/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The precedence step that decided the request.
 */
export const ScopeSource = {
  ResourcePin: "resource_pin",
  ClientScope: "client_scope",
  ChallengeScope: "challenge_scope",
  LiveResource: "live_resource",
  CachedResource: "cached_resource",
  IssuerOverride: "issuer_override",
  IssuerOmitted: "issuer_omitted",
  IssuerCatalogue: "issuer_catalogue",
  None: "none",
} as const;
/**
 * The precedence step that decided the request.
 */
export type ScopeSource = OpenEnum<typeof ScopeSource>;

/**
 * What a login through one client bound to the MCP server would request now, resolved from cached state.
 */
export type RemoteMcpServerClientScopes = {
  /**
   * The remote session client's ID.
   */
  clientId: string;
  /**
   * The configured name of the client's authorization server; absent when it has none.
   */
  issuerName?: string | undefined;
  /**
   * The issuer URL of the client's authorization server.
   */
  issuerUrl?: string | undefined;
  /**
   * Whether a pin, if set, decides this client's request: discovery is on, the client owns the resource, and neither its own scope nor a challenge outranks the pin.
   */
  pinWouldDecide: boolean;
  /**
   * The scope parameter the login would send; empty sends none.
   */
  requestedScopes: Array<string>;
  /**
   * The precedence step that decided the request.
   */
  scopeSource: ScopeSource;
  /**
   * Pinned scopes the resource does not advertise; sent regardless. Empty when the pin does not decide or the resource's list is unknown.
   */
  unadvertisedPinnedScopes: Array<string>;
};

/** @internal */
export const ScopeSource$inboundSchema: z.ZodMiniType<ScopeSource, unknown> =
  openEnums.inboundSchema(ScopeSource);

/** @internal */
export const RemoteMcpServerClientScopes$inboundSchema: z.ZodMiniType<
  RemoteMcpServerClientScopes,
  unknown
> = z.pipe(
  z.object({
    client_id: z.string(),
    issuer_name: z.optional(z.string()),
    issuer_url: z.optional(z.string()),
    pin_would_decide: z.boolean(),
    requested_scopes: z.array(z.string()),
    scope_source: ScopeSource$inboundSchema,
    unadvertised_pinned_scopes: z.array(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "client_id": "clientId",
      "issuer_name": "issuerName",
      "issuer_url": "issuerUrl",
      "pin_would_decide": "pinWouldDecide",
      "requested_scopes": "requestedScopes",
      "scope_source": "scopeSource",
      "unadvertised_pinned_scopes": "unadvertisedPinnedScopes",
    });
  }),
);

export function remoteMcpServerClientScopesFromJSON(
  jsonString: string,
): SafeParseResult<RemoteMcpServerClientScopes, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RemoteMcpServerClientScopes$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RemoteMcpServerClientScopes' from JSON`,
  );
}
